import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { ToastService } from '@ui-lib/components';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, finalize, Observable, of, shareReplay } from 'rxjs';
import { LoadingState } from '../../shared/types/loading.type';
import { outputError } from '../../shared/utils/output-error.function';
import { MaintenanceStatusClient } from '../../core/openapi/bff-admin-api/api/maintenance-status.client';
import { MaintenanceErrorClient } from '../../core/openapi/bff-admin-api/api/maintenance-error.client';
import {
  DictionaryResponse,
  MaintenanceTypeClient,
  WarrantyTypeClient,
} from '../../core/openapi/bff-admin-api';

@Injectable({ providedIn: 'root' })
export class MaintenanceDictionaryService {
  private readonly maintenanceStatusClient = inject(MaintenanceStatusClient);
  private readonly maintenanceTypeClient = inject(MaintenanceTypeClient);
  private readonly warrantyTypeClient = inject(WarrantyTypeClient);
  private readonly maintenanceErrorClient = inject(MaintenanceErrorClient);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  private readonly _statusesLoadingStatus: WritableSignal<LoadingState> = signal(LoadingState.IDLE);
  private readonly _maintenanceTypesLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.IDLE,
  );
  private readonly _warrantyTypesLoadingStatus: WritableSignal<LoadingState> = signal(
    LoadingState.IDLE,
  );
  private readonly _errorsLoadingStatus: WritableSignal<LoadingState> = signal(LoadingState.IDLE);

  public readonly statusesLoadingStatus: Signal<LoadingState> =
    this._statusesLoadingStatus.asReadonly();
  public readonly maintenanceTypesLoadingStatus: Signal<LoadingState> =
    this._maintenanceTypesLoadingStatus.asReadonly();
  public readonly warrantyTypesLoadingStatus: Signal<LoadingState> =
    this._warrantyTypesLoadingStatus.asReadonly();
  public readonly errorsLoadingStatus: Signal<LoadingState> =
    this._errorsLoadingStatus.asReadonly();

  public readonly statuses$: Observable<DictionaryResponse[]> = defer(() => {
    this._statusesLoadingStatus.set(LoadingState.LOADING);
    return this.maintenanceStatusClient.v1MaintenanceStatusGet();
  }).pipe(
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.maintenance.toast.maintenance-statuses-fetching-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of([]);
    }),
    finalize(() => this._statusesLoadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );

  public readonly warrantyTypes$: Observable<DictionaryResponse[]> = defer(() => {
    this._warrantyTypesLoadingStatus.set(LoadingState.LOADING);
    return this.warrantyTypeClient.v1WarrantyTypeGet();
  }).pipe(
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.maintenance.toast.maintenance-warranty-types-fetching-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of([]);
    }),
    finalize(() => this._warrantyTypesLoadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );

  public readonly maintenanceTypes$: Observable<DictionaryResponse[]> = defer(() => {
    this._maintenanceTypesLoadingStatus.set(LoadingState.LOADING);
    return this.maintenanceTypeClient.v1MaintenanceTypeGet();
  }).pipe(
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.maintenance.toast.maintenance-types-fetching-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of([]);
    }),
    finalize(() => this._maintenanceTypesLoadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );

  public readonly errors$: Observable<DictionaryResponse[]> = defer(() => {
    this._errorsLoadingStatus.set(LoadingState.LOADING);
    return this.maintenanceErrorClient.v1MaintenanceErrorGet();
  }).pipe(
    catchError((error) => {
      this.toastService.error({
        message: this.translateService.instant(
          'monitoring.maintenance.modal.toast.errors-fetch-failed',
          { error: outputError(error, this.translateService.instant('errors.unknown')) },
        ),
      });
      return of([]);
    }),
    finalize(() => this._errorsLoadingStatus.set(LoadingState.READY)),
    shareReplay(1),
  );
}
